import { IconButton } from '@ui/shared/Button';
import some from 'lodash/some';
import * as React from 'react';
import { LIST_ROW_HEIGHTS } from '../../../../../constants/list_row_heights';
import {
  IAthlete,
  IListRowRenderProps,
  ITeam,
} from '../../../../../data/types';
import { ScrollableArea } from '../../../../layout/ScrollableArea';
import { List } from '../../../../shared/List/List';
import { ListAvatar } from '../../../../shared/List/ListAvatar';
import { ListControlContainer } from '../../../../shared/List/ListControlContainer';
import { ListItem, ListItemCell } from '../../../../shared/List/ListItem';
import { NoResults } from '../../../../shared/NoResults';

interface IProps {
  importList: Array<Partial<IAthlete>>;
  removeItem: (index: number) => void;
  selectedTeams: ITeam[];
  errorItems?: Array<Partial<IAthlete>>;
}

const ImportList = (props: IProps): React.ReactElement => {
  const isDuplicate = (
    item: Partial<IAthlete>,
    errors: Array<Partial<IAthlete>> = []
  ): boolean => {
    return some(errors, (error: Partial<IAthlete>): boolean => {
      if (item.name === error.name) return true;
      if (item?.external?.uniqueId && error.external?.uniqueId) {
        return item.external.uniqueId === error.external.uniqueId;
      }
      return false;
    });
  };
  /**
   * @description render function for athlete list items
   * @param param0
   */
  const rowRenderer = ({
    index,
    key,
    style,
  }: IListRowRenderProps): React.ReactElement => {
    const athlete = props.importList[index];
    const error = isDuplicate(athlete, props.errorItems);

    return (
      <ImportListItem
        key={key}
        athlete={athlete}
        index={index}
        style={style}
        onRemove={props.removeItem}
        error={error ? 'This athlete is a duplicate' : null}
      />
    );
  };

  return (
    <ListControlContainer>
      <ScrollableArea style={{ flex: 1 }}>
        {props.selectedTeams?.length === 0 ? (
          <NoResults message='Select a team to import athletes' />
        ) : props.importList?.length === 0 ? (
          <NoResults message='No Athletes to Import' />
        ) : (
          <List
            listLength={props.importList.length}
            rowHeight={LIST_ROW_HEIGHTS.normal}
            rowRenderer={rowRenderer}
            roundedContainer
          />
        )}
      </ScrollableArea>
    </ListControlContainer>
  );
};

export { ImportList };

interface IListProps {
  athlete: Partial<IAthlete>;
  onRemove: (index: number) => void;
  index: number;
  style?: React.CSSProperties;
  error?: string;
}

const ImportListItem = (props: IListProps): React.ReactElement => {
  const removeHandler = (): void => {
    props.onRemove(props.index);
  };

  return (
    <ListItem style={props.style}>
      <ListItemCell style={{ flex: 2 }}>
        <ListAvatar entity={props.athlete} error={props.error} />
      </ListItemCell>
      <ListItemCell>
        <IconButton icon='clear' onClick={removeHandler} title='Remove' />
      </ListItemCell>
    </ListItem>
  );
};
